import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

import ReportHeader from "../cash-register/ReportHeader";
import ReportFooter from "../cash-register/ReportFooter";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";

export interface IOrderManifestRow {
  invoiceNumber: string;
  department: string;
  pieceCount: number;
}

export interface OrderManifestReportProps {
  customerName: string;
  customerId: string;
  rows: IOrderManifestRow[];
  reportDate?: Date;
}

const styles = StyleSheet.create({
  page: { fontSize: 12, fontFamily: "Helvetica" },
  tableContainer: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: "#A1CEF5",
    borderRadius: 3,
  },
  tableHeader: {
    backgroundColor: "#A1CEF5",
    flexDirection: "row",
    padding: 6,
  },
  tableRow: { flexDirection: "row", padding: 6 },
  cell: { fontSize: 8, textAlign: "center", padding: 4 },
  colNo: { width: "10%" },
  colInvoice: { width: "30%" },
  colDepartment: { width: "30%" },
  colPieces: { width: "20%" },
  colCheck: { width: "10%" },
  infoRow: { flexDirection: "row", marginBottom: 4 },
  infoLabel: { width: "35%", fontSize: 8 },
  infoColon: { width: "5%", fontSize: 8, textAlign: "center" },
  infoValue: { width: "60%", fontSize: 8, paddingLeft: 8 },
});

const chunkArray = <T,>(arr: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );

const formatManifestDate = (date: Date) => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const OrderManifestReport = ({
  customerName,
  customerId,
  rows,
  reportDate = new Date(),
}: OrderManifestReportProps) => {
  const totalPieces = rows.reduce((sum, row) => sum + row.pieceCount, 0);
  const chunks = chunkArray(
    rows.length > 0 ? rows : [{ invoiceNumber: "—", department: "—", pieceCount: 0 }],
    20
  );

  return (
    <Document>
      {chunks.map((chunk, pageIndex, pages) => (
        <Page size="A4" style={styles.page} key={pageIndex}>
          <ReportHeader />
          <View
            style={{
              width: "518px",
              backgroundColor: Colors.REPORT_WHITE,
              borderRadius: 15,
              marginLeft: 48,
              padding: 18,
            }}
          >
            <View style={{ flexDirection: "column", height: pxToRem(710) }}>
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: pageIndex === 0 ? 12 : 30,
                }}
              >
                <Text style={{ fontSize: 14, fontWeight: "bold", color: "#505050" }}>
                  {pageIndex === 0 ? "Order Manifest" : "Order Manifest (Contd.)"}
                </Text>
                {pageIndex === 0 && (
                  <Text style={{ fontSize: 14, fontWeight: "bold", color: "#505050" }}>
                    {formatManifestDate(reportDate)}
                  </Text>
                )}
              </View>

              {pageIndex === 0 && (
                <View style={{ marginBottom: 12 }}>
                  {[
                    ["Customer Name", customerName],
                    ["Customer ID", customerId],
                    ["Total no. of orders", `${rows.length}`],
                    ["Total no. of pieces", `${totalPieces}`],
                  ].map(([label, value]) => (
                    <View key={label} style={styles.infoRow}>
                      <Text style={styles.infoLabel}>{label}</Text>
                      <Text style={styles.infoColon}>:</Text>
                      <Text style={styles.infoValue}>{value}</Text>
                    </View>
                  ))}
                </View>
              )}

              <View style={styles.tableContainer}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.cell, styles.colNo]}>No.</Text>
                  <Text style={[styles.cell, styles.colInvoice]}>Invoice Number</Text>
                  <Text style={[styles.cell, styles.colDepartment]}>Department</Text>
                  <Text style={[styles.cell, styles.colPieces]}>Piece Count</Text>
                  <Text style={[styles.cell, styles.colCheck]}>Check</Text>
                </View>
                {chunk.map((row, idx) => (
                  <View key={idx} style={styles.tableRow}>
                    <Text style={[styles.cell, styles.colNo]}>
                      {String(pageIndex * 20 + idx + 1).padStart(2, "0")}
                    </Text>
                    <Text style={[styles.cell, styles.colInvoice]}>{row.invoiceNumber}</Text>
                    <Text style={[styles.cell, styles.colDepartment]}>{row.department}</Text>
                    <Text style={[styles.cell, styles.colPieces]}>{row.pieceCount}</Text>
                    <View style={[styles.colCheck, { alignItems: "center" }]}>
                      <View
                        style={{
                          width: 9,
                          height: 9,
                          borderWidth: 1,
                          borderColor: "#9CA3AF",
                        }}
                      />
                    </View>
                  </View>
                ))}
                {pageIndex === pages.length - 1 && (
                  <View style={[styles.tableRow, { backgroundColor: Colors.REPORT_BLUE }]}>
                    <Text style={[styles.cell, styles.colNo]}>Total</Text>
                    <Text style={[styles.cell, styles.colInvoice]} />
                    <Text style={[styles.cell, styles.colDepartment]} />
                    <Text style={[styles.cell, styles.colPieces]}>{totalPieces}</Text>
                    <Text style={[styles.cell, styles.colCheck]} />
                  </View>
                )}
              </View>

              {pageIndex === pages.length - 1 && (
                <View
                  style={{
                    marginTop: "auto",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  {["Checked by", "Verified by"].map((label) => (
                    <View key={label} style={{ width: "45%" }}>
                      <Text style={{ fontSize: 9, color: "#505050", marginBottom: 14 }}>
                        {label}:
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          marginBottom: 12,
                          borderBottomWidth: 0.5,
                          borderBottomColor: "#9CA3AF",
                          paddingBottom: 2,
                        }}
                      >
                        <Text style={{ fontSize: 8, color: "#505050" }}>Name:</Text>
                      </View>
                      <View
                        style={{
                          flexDirection: "row",
                          borderBottomWidth: 0.5,
                          borderBottomColor: "#9CA3AF",
                          paddingBottom: 2,
                        }}
                      >
                        <Text style={{ fontSize: 8, color: "#505050" }}>Date:</Text>
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
          <ReportFooter />
        </Page>
      ))}
    </Document>
  );
};

export default OrderManifestReport;
